import { Typography, Box, SxProps, Theme } from '@mui/material';

import { BaseNugget } from './BaseNugget';
import { SponsorChip, SponsorChipProps } from './SponsorChip';
import { TypeChip, TypeChipProps } from './TypeChip';

type LineItem = Readonly<{
  title: string;
  text: string;
}>;

type NuggetGenericProps = Readonly<{
  title: string;
  customHeader?: React.ReactNode;
  typeChipProps?: TypeChipProps[];
  sponsorChipProps?: SponsorChipProps[];
  description?: string;
  lineItems?: LineItem[];
  coverImage?: string;
  onClick?: () => void;
  subText?: string;
  hideBody: boolean;
  fitContent: boolean;
}>;

export const NuggetGeneric = ({
  title,
  customHeader,
  typeChipProps,
  sponsorChipProps,
  description,
  lineItems,
  coverImage,
  onClick,
  subText,
  hideBody,
  fitContent,
}: NuggetGenericProps) => {
  return (
    <BaseNugget subText={subText} onClick={onClick} fitContent={fitContent}>
      {coverImage && <Box component="img" src={coverImage} alt={title} sx={coverImageSx} />}
      {customHeader && customHeader}
      <Box sx={contentOuterSx}>
        {sponsorChipProps && (
          <Box sx={sponsorChipContainerSx}>
            {sponsorChipProps.map((sponsor) => (
              <SponsorChip
                key={sponsor.label}
                label={sponsor.label}
                iconImage={sponsor.iconImage}
                defaultIcon={sponsor.defaultIcon}
              />
            ))}
          </Box>
        )}
        <Box sx={contentInnerSx}>
          <Box sx={bodyContainerSx}>
            <Typography variant="text-lg-semibold" sx={titleSx}>
              {title}
            </Typography>
            {!hideBody && (
              <Typography variant="text-md-regular" sx={bodyTextStyle}>
                {description}
              </Typography>
            )}
            {lineItems?.map((item) => (
              <Box key={item.title} sx={lineItemSx}>
                <Typography variant="text-md-regular" sx={lineItemTitleSx}>
                  {item.title}:&nbsp;
                </Typography>
                <Typography variant="text-md-regular" sx={lineItemTextSx}>
                  {item.text}
                </Typography>
              </Box>
            ))}
          </Box>
          <Box sx={typeChipContainerSx}>
            {typeChipProps &&
              typeChipProps.map((type) => (
                <TypeChip key={type.label} label={type.label} icon={type.icon} />
              ))}
          </Box>
        </Box>
      </Box>
    </BaseNugget>
  );
};

const coverImageSx: SxProps<Theme> = {
  width: '100%',
  height: '200px',
  minHeight: '200px',
  maxHeight: '200px',
  objectFit: 'cover',
};

const contentOuterSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
  paddingTop: backrs.spacing['spacing-2xl'],
  paddingBottom: backrs.spacing['spacing-2xl'],
  paddingLeft: backrs.spacing['spacing-xl'],
  paddingRight: backrs.spacing['spacing-xl'],
  height: '100%',
});

const contentInnerSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
};

const bodyContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-xxs'],
});

const titleSx: SxProps<Theme> = {
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 1,
  textOverflow: 'ellipsis',
};

const bodyTextStyle: SxProps<Theme> = {
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 5,
  textOverflow: 'ellipsis',
};

const lineItemSx: SxProps<Theme> = {
  display: 'flex',
  gap: 0,
};

const lineItemTitleSx: SxProps<Theme> = {
  fontStyle: 'italic',
  m: 0,
  p: 0,
};

const lineItemTextSx: SxProps<Theme> = {
  m: 0,
  p: 0,
};

const typeChipContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  gap: backrs.spacing['spacing-lg'],
  flexDirection: 'row',
  flexWrap: 'wrap',
  marginTop: backrs.spacing['spacing-xl'],
});

const sponsorChipContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  columnGap: backrs.spacing['spacing-lg'],
  flexDirection: 'row',
  flexWrap: 'wrap',
  rowGap: backrs.spacing['spacing-xs'],
  width: '100%',
});
